import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { memberAPI, relationshipAPI, accessAPI, relationshipRecommAPI } from '../services/api';
import { useData } from '../contexts/DataContext';
import AppHeader from './AppHeader';
import ConfirmDialog from './ConfirmDialog';
import Avatar from './Avatar';
import CollapsibleSection from './CollapsibleSection';
import InviteModal from './InviteModal';
import RelationshipRecomm from './RelationshipRecomm';
import './MemberDetail.css';

// Helper to format date strings without timezone conversion
// Parses "YYYY-MM-DD" and displays in local format without shifting days
function formatDate(dateString) {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed
  return date.toLocaleDateString();
}

function MemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { members: cachedMembers, refreshRelationships, refreshMembers } = useData();
  const [member, setMember] = useState(null);
  const [events, setEvents] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [accessList, setAccessList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRole, setMyRole] = useState(null); // 'admin', 'edit', or null (view-only)
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [recommendations, setRecommendations] = useState([]);

  // Get IDs of people who already have a relationship with the current member
  const existingRelativeIds = useMemo(() => {
    const ids = new Set();
    relationships.forEach(rel => {
      if (rel.person1_id === parseInt(id)) {
        ids.add(rel.person2_id);
      } else if (rel.person2_id === parseInt(id)) {
        ids.add(rel.person1_id);
      }
    });
    return ids;
  }, [relationships, id]);

  // Use cached members for dropdowns, filtered to exclude current member and existing relatives
  const allMembers = useMemo(() =>
    cachedMembers.filter(m => m.id !== parseInt(id) && !existingRelativeIds.has(m.id)),
    [cachedMembers, id, existingRelativeIds]
  );
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showRelationshipForm, setShowRelationshipForm] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [accessError, setAccessError] = useState('');
  const [eventData, setEventData] = useState({
    event_type: '',
    event_date: '',
    location: '',
    description: '',
  });
  const [relationshipData, setRelationshipData] = useState({
    person2_id: '',
    relationship_type: '',
    marriage_date: '',
    divorce_date: '',
  });
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [editingEventIndex, setEditingEventIndex] = useState(null);

  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    gender: '',
    maiden_name: '',
    date_of_birth: '',
    place_of_birth: '',
    date_of_death: '',
    place_of_death: '',
  });

  // Biography editing state
  const [editingBiography, setEditingBiography] = useState(false);
  const [biographyText, setBiographyText] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [memberRes, relationshipsRes, accessRes, roleRes, recommRes] = await Promise.all([
        memberAPI.getById(id),
        relationshipAPI.getAll(id),
        accessAPI.getList(id),
        accessAPI.getMyRole(id),
        relationshipRecommAPI.getAll(id),
      ]);
      setMember(memberRes.data);
      setEvents(memberRes.data.events || []);
      setRelationships(relationshipsRes.data);
      setAccessList(accessRes.data);
      setMyRole(roleRes.data.role);
      setRecommendations(recommRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    setAccessError('');
    try {
      await accessAPI.grant({ email: grantEmail, member_id: id });
      setGrantEmail('');
      setShowAccessForm(false);
      fetchData();
    } catch (err) {
      setAccessError(err.response?.data?.error || 'Failed to grant access');
    }
  };

  const handleEditEvent = (eventIndex) => {
    const event = events[eventIndex];
    setEventData({
      event_type: event.name || '',
      event_date: event.date || '',
      location: event.place || '',
      description: event.comment || '',
    });
    setEditingEventIndex(eventIndex);
    setShowEventForm(true);
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      const eventToSave = {
        name: eventData.event_type,
        date: eventData.event_date,
        place: eventData.location,
        comment: eventData.description,
      };

      let updatedEvents;
      if (editingEventIndex !== null) {
        updatedEvents = events.map((event, i) => i === editingEventIndex ? eventToSave : event);
      } else {
        updatedEvents = [...events, eventToSave];
      }

      const formData = new FormData();
      formData.append('events', JSON.stringify(updatedEvents));
      await memberAPI.update(id, formData);
      setEventData({ event_type: '', event_date: '', location: '', description: '' });
      setEditingEventIndex(null);
      setShowEventForm(false);
      fetchData();
      refreshMembers(); // Update global cache for other views
    } catch (err) {
      alert('Failed to save event');
    }
  };

  const handleCancelEventForm = () => {
    setEventData({ event_type: '', event_date: '', location: '', description: '' });
    setEditingEventIndex(null);
    setShowEventForm(false);
  };

  const handleDeleteEvent = (eventIndex) => {
    setConfirmDialog({
      title: 'Delete Event',
      message: 'Are you sure you want to delete this event?',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          const updatedEvents = events.filter((_, i) => i !== eventIndex);
          const formData = new FormData();
          formData.append('events', JSON.stringify(updatedEvents));
          await memberAPI.update(id, formData);
          fetchData();
          refreshMembers(); // Update global cache for other views
        } catch (err) {
          alert('Failed to delete event');
        }
      },
      onCancel: () => setConfirmDialog(null),
    });
  };

  const handleAddRelationship = async (e) => {
    e.preventDefault();
    try {
      await relationshipAPI.create({
        person1_id: id,
        person2_id: relationshipData.person2_id,
        relationship_type: relationshipData.relationship_type,
        marriage_date: relationshipData.marriage_date || null,
        divorce_date: relationshipData.divorce_date || null,
      });
      setRelationshipData({ person2_id: '', relationship_type: '', marriage_date: '', divorce_date: '' });
      setShowRelationshipForm(false);
      fetchData();
      await refreshRelationships(); // Update global cache
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add relationship');
    }
  };

  const handleDeleteRelationship = (relationshipId) => {
    setConfirmDialog({
      title: 'Delete Relationship',
      message: 'Are you sure you want to delete this relationship?',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await relationshipAPI.delete(relationshipId);
          fetchData();
          refreshRelationships(); // Update global cache
        } catch (err) {
          alert('Failed to delete relationship');
        }
      },
      onCancel: () => setConfirmDialog(null),
    });
  };

  const handleApplyRecommendation = async (recommId) => {
    try {
      await relationshipRecommAPI.apply(recommId);
      fetchData();
      refreshRelationships();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to apply recommendation');
    }
  };

  const handleDismissRecommendation = async (recommId) => {
    try {
      await relationshipRecommAPI.dismiss(recommId);
      setRecommendations(recommendations.filter(r => r.id !== recommId));
    } catch (err) {
      alert('Failed to dismiss recommendation');
    }
  };

  const handleEditProfile = () => {
    setProfileForm({
      first_name: member.first_name || '',
      middle_name: member.middle_name || '',
      last_name: member.last_name || '',
      gender: member.details?.gender || '',
      maiden_name: member.details?.maiden_name || '',
      date_of_birth: member.date_of_birth || '',
      place_of_birth: member.place_of_birth || '',
      date_of_death: member.date_of_death || '',
      place_of_death: member.place_of_death || '',
    });
    setEditingProfile(true);
  };

  const handleCancelProfileEdit = () => {
    setEditingProfile(false);
  };

  const handleSaveProfile = async () => {
    try {
      const formData = new FormData();
      formData.append('first_name', profileForm.first_name);
      formData.append('middle_name', profileForm.middle_name);
      formData.append('last_name', profileForm.last_name);
      formData.append('date_of_birth', profileForm.date_of_birth);
      formData.append('place_of_birth', profileForm.place_of_birth);
      formData.append('date_of_death', profileForm.date_of_death);
      formData.append('place_of_death', profileForm.place_of_death);

      // Merge gender and maiden_name into existing details
      const updatedDetails = {
        ...(member.details || {}),
        gender: profileForm.gender,
        maiden_name: profileForm.maiden_name,
      };
      formData.append('details', JSON.stringify(updatedDetails));

      await memberAPI.update(id, formData);
      setEditingProfile(false);
      fetchData();
      refreshMembers(); // Update global cache for other views
    } catch (err) {
      alert('Failed to save profile');
    }
  };

  const handlePhotoUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      await memberAPI.update(id, formData);
      fetchData();
      refreshMembers(); // Update global cache for other views
    } catch (err) {
      alert('Failed to upload photo');
    }
  };

  const handlePhotoRemove = async () => {
    try {
      await memberAPI.removePhoto(id);
      fetchData();
      refreshMembers();
    } catch (err) {
      alert('Failed to remove photo');
    }
  };

  const handleProfileChange = (e) => {
    setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
  };

  const handleEditBiography = () => {
    setBiographyText(member.biography || '');
    setEditingBiography(true);
  };

  const handleSaveBiography = async () => {
    try {
      const formData = new FormData();
      formData.append('biography', biographyText);
      await memberAPI.update(id, formData);
      setEditingBiography(false);
      fetchData();
      refreshMembers(); // Update global cache for other views
    } catch (err) {
      alert('Failed to save biography');
    }
  };

  const handleCancelBiography = () => {
    setEditingBiography(false);
  };

  const handleDeleteMember = () => {
    setConfirmDialog({
      title: 'Delete Member',
      message: `Are you sure you want to delete ${member.first_name} ${member.last_name}? This action cannot be undone.`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await memberAPI.delete(id);
          await refreshMembers();
          await refreshRelationships();
          navigate('/');
        } catch (err) {
          alert('Failed to delete member');
        }
      },
      onCancel: () => setConfirmDialog(null),
    });
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!member) return <div className="error">Member not found</div>;

  const canEdit = myRole === 'admin' || myRole === 'edit';
  const isAdmin = myRole === 'admin';
  const isUnclaimed = !member.user_id;

  return (
    <div className="member-detail">
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}
      <AppHeader />

      <div className="detail-content">
        <div className="member-profile">
          <Avatar
            photoUrl={member.photo_url}
            firstName={member.first_name}
            lastName={member.last_name}
            size={150}
            editable={canEdit}
            onClick={canEdit ? handlePhotoUpload : undefined}
            onRemove={canEdit ? handlePhotoRemove : undefined}
          />
          <div className="profile-info">
            {editingProfile ? (
              <div className="profile-edit-form">
                <div className="profile-form-row">
                  <input
                    type="text"
                    name="first_name"
                    value={profileForm.first_name}
                    onChange={handleProfileChange}
                    placeholder="First name"
                    className="profile-input"
                  />
                  <input
                    type="text"
                    name="middle_name"
                    value={profileForm.middle_name}
                    onChange={handleProfileChange}
                    placeholder="Middle name"
                    className="profile-input"
                  />
                  <input
                    type="text"
                    name="last_name"
                    value={profileForm.last_name}
                    onChange={handleProfileChange}
                    placeholder="Last name"
                    className="profile-input"
                  />
                </div>
                <div className="profile-form-row">
                  <select
                    name="gender"
                    value={profileForm.gender}
                    onChange={handleProfileChange}
                    className="profile-input"
                  >
                    <option value="">Select gender...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  <input
                    type="text"
                    name="maiden_name"
                    value={profileForm.maiden_name}
                    onChange={handleProfileChange}
                    placeholder="Maiden name"
                    className="profile-input"
                  />
                </div>
                <div className="profile-form-row">
                  <div className="profile-field-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={profileForm.date_of_birth}
                      onChange={handleProfileChange}
                      className="profile-input"
                    />
                  </div>
                  <input
                    type="text"
                    name="place_of_birth"
                    value={profileForm.place_of_birth}
                    onChange={handleProfileChange}
                    placeholder="Place of birth"
                    className="profile-input"
                  />
                </div>
                <div className="profile-form-row">
                  <div className="profile-field-group">
                    <label>Date of Death</label>
                    <input
                      type="date"
                      name="date_of_death"
                      value={profileForm.date_of_death}
                      onChange={handleProfileChange}
                      className="profile-input"
                    />
                  </div>
                  <input
                    type="text"
                    name="place_of_death"
                    value={profileForm.place_of_death}
                    onChange={handleProfileChange}
                    placeholder="Place of death"
                    className="profile-input"
                  />
                </div>
                <div className="profile-form-actions">
                  <button onClick={handleSaveProfile} className="btn btn-primary">Save</button>
                  <button onClick={handleCancelProfileEdit} className="btn btn-secondary">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div className="profile-header-row">
                  <h1>{member.first_name} {member.middle_name} {member.last_name}</h1>
                  {canEdit && (
                    <button onClick={handleEditProfile} className="btn-icon" title="Edit profile">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  )}
                </div>
                {member.details?.maiden_name && <p className="maiden-name">Maiden name: {member.details.maiden_name}</p>}
                {member.details?.gender && <p><strong>Gender:</strong> {member.details.gender}</p>}
                {member.date_of_birth && (
                  <p><strong>Born:</strong> {formatDate(member.date_of_birth)}
                    {member.place_of_birth && ` in ${member.place_of_birth}`}
                  </p>
                )}
                {member.date_of_death && (
                  <p><strong>Died:</strong> {formatDate(member.date_of_death)}
                    {member.place_of_death && ` in ${member.place_of_death}`}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <CollapsibleSection
          title="Biography"
          name="biography"
          defaultExpanded={true}
        >
          <div className="biography-section">
            {editingBiography ? (
              <div className="biography-edit">
                <textarea
                  value={biographyText}
                  onChange={(e) => setBiographyText(e.target.value)}
                  rows="6"
                  placeholder="Enter biography..."
                  className="biography-textarea"
                />
                <div className="biography-actions">
                  <button onClick={handleSaveBiography} className="btn btn-primary">Save</button>
                  <button onClick={handleCancelBiography} className="btn btn-secondary">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="biography-view">
                <p>{member.biography || 'No biography yet.'}</p>
                {canEdit && <button onClick={handleEditBiography} className="btn btn-small">Edit</button>}
              </div>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title="Life Events"
          name="events"
          defaultExpanded={true}
        >
          <div className="section-content">
            <div className="section-actions">
              {canEdit && !showEventForm && (
                <button onClick={() => setShowEventForm(true)} className="btn btn-small">
                  Add Event
                </button>
              )}
            </div>

            {showEventForm && (
              <form onSubmit={handleSaveEvent} className="inline-form">
                <input
                  type="text"
                  placeholder="Event type"
                  value={eventData.event_type}
                  onChange={(e) => setEventData({ ...eventData, event_type: e.target.value })}
                  required
                />
                <input
                  type="date"
                  value={eventData.event_date}
                  onChange={(e) => setEventData({ ...eventData, event_date: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="Location"
                  value={eventData.location}
                  onChange={(e) => setEventData({ ...eventData, location: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="Description"
                  value={eventData.description}
                  onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                />
                <button type="submit" className="btn btn-primary">
                  {editingEventIndex !== null ? 'Save' : 'Add'}
                </button>
                <button type="button" onClick={handleCancelEventForm} className="btn btn-secondary">
                  Cancel
                </button>
              </form>
            )}

            {events.length === 0 ? (
              <p>No events recorded yet.</p>
            ) : (
              <div className="events-list">
                {[...events]
                  .map((event, index) => ({ ...event, originalIndex: index }))
                  .sort((a, b) => {
                    if (!a.date && !b.date) return 0;
                    if (!a.date) return 1;
                    if (!b.date) return -1;
                    return new Date(b.date) - new Date(a.date);
                  })
                  .map(event => (
                    <div key={event.originalIndex} className="event-row">
                      <span className="event-name">{event.name}</span>
                      <span className="event-date">
                        {[event.place, event.date].filter(Boolean).join(', ') || '-'}
                      </span>
                      <span className="event-description">{event.comment || '-'}</span>
                      {canEdit && (
                        <div className="event-actions">
                          <button onClick={() => handleEditEvent(event.originalIndex)} className="btn btn-small">Edit</button>
                          <button onClick={() => handleDeleteEvent(event.originalIndex)} className="btn btn-small btn-danger">Delete</button>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title="Relationships"
          name="relationships"
          defaultExpanded={true}
        >
          <div className="section-content">
            <div className="section-actions">
              {canEdit && (
                <button onClick={() => setShowRelationshipForm(!showRelationshipForm)} className="btn btn-small">
                  {showRelationshipForm ? 'Cancel' : 'Add Relationship'}
                </button>
              )}
            </div>

            {showRelationshipForm && (
              <form onSubmit={handleAddRelationship} className="inline-form">
                <select
                  value={relationshipData.person2_id}
                  onChange={(e) => setRelationshipData({ ...relationshipData, person2_id: e.target.value })}
                  required
                >
                  <option value="">Select family member...</option>
                  {allMembers.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.first_name} {m.last_name}
                    </option>
                  ))}
                </select>
                <select
                  value={relationshipData.relationship_type}
                  onChange={(e) => setRelationshipData({ ...relationshipData, relationship_type: e.target.value })}
                  required
                >
                  <option value="">Select relationship...</option>
                  <option value="Parent">Parent</option>
                  <option value="Child">Child</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Grandparent">Grandparent</option>
                  <option value="Grandchild">Grandchild</option>
                  <option value="Uncle/Aunt">Uncle/Aunt</option>
                  <option value="Nephew/Niece">Nephew/Niece</option>
                  <option value="Cousin">Cousin</option>
                </select>
                <input
                  type="date"
                  placeholder="Marriage date (optional)"
                  value={relationshipData.marriage_date}
                  onChange={(e) => setRelationshipData({ ...relationshipData, marriage_date: e.target.value })}
                />
                <button type="submit" className="btn btn-primary">Add</button>
              </form>
            )}

            {relationships.length === 0 ? (
              <p>No relationships recorded yet.</p>
            ) : (
              <div className="relationships-list">
                {relationships.map(rel => {
                  const currentMemberId = parseInt(id);
                  const isCurrentMemberTheParent = rel.person1_id === currentMemberId;
                  const relatedMemberId = isCurrentMemberTheParent ? rel.person2_id : rel.person1_id;
                  const relatedMemberName = isCurrentMemberTheParent
                    ? `${rel.person2_first_name} ${rel.person2_last_name}`
                    : `${rel.person1_first_name} ${rel.person1_last_name}`;

                  // For parent relationships: person1 is parent, person2 is child
                  // Display the role of the *related* person from current member's perspective
                  let relationshipLabel = rel.relationship_type;
                  if (rel.relationship_type.toLowerCase() === 'parent') {
                    relationshipLabel = isCurrentMemberTheParent ? 'child' : 'parent';
                  }

                  return (
                    <div key={rel.id} className="relationship-item">
                      <div className="relationship-info">
                        <p>
                          <Link to={`/member/${relatedMemberId}`} className="relationship-link">
                            <strong>{relatedMemberName}</strong>
                          </Link> ({relationshipLabel})
                        </p>
                        {rel.marriage_date && (
                          <p className="relationship-date">Married: {formatDate(rel.marriage_date)}</p>
                        )}
                        {rel.divorce_date && (
                          <p className="relationship-date">Divorced: {formatDate(rel.divorce_date)}</p>
                        )}
                      </div>
                      {isAdmin && (
                        <button onClick={() => handleDeleteRelationship(rel.id)} className="btn btn-small btn-danger">Delete</button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CollapsibleSection>

        {recommendations.length > 0 && canEdit && (
          <CollapsibleSection
            title="Suggested Relationships"
            name="suggestions"
            defaultExpanded={true}
          >
            <div className="section-content">
              {recommendations.map(recomm => (
                <RelationshipRecomm
                  key={recomm.id}
                  recommendation={recomm}
                  onApply={handleApplyRecommendation}
                  onDismiss={handleDismissRecommendation}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {isAdmin && (
          <CollapsibleSection
            title="Admin"
            name="admin"
            defaultExpanded={false}
          >
            <div className="admin-section">
              {isUnclaimed && (
                <div className="admin-subsection">
                  <h3>Invite to Claim</h3>
                  <p>This family member is not yet claimed by a user. Send an invitation to let someone claim this profile.</p>
                  <button onClick={() => setShowInviteModal(true)} className="btn btn-primary">
                    Create Invitation
                  </button>
                </div>
              )}

              <div className="admin-subsection">
                <h3>Access Control</h3>
                <div className="section-actions">
                  <button onClick={() => setShowAccessForm(!showAccessForm)} className="btn btn-small">
                    {showAccessForm ? 'Cancel' : 'Grant Access'}
                  </button>
                </div>
                {showAccessForm && (
                  <>
                    {accessError && <div className="error-message">{accessError}</div>}
                    <form onSubmit={handleGrantAccess} className="inline-form">
                      <input
                        type="email"
                        placeholder="Email"
                        value={grantEmail}
                        onChange={(e) => setGrantEmail(e.target.value)}
                        required
                      />
                      <button type="submit" className="btn btn-primary">Grant</button>
                    </form>
                  </>
                )}
                <div className="access-list">
                  {accessList.map(access => (
                    <div key={access.id} className="access-item">
                      <span>{access.username} ({access.email})</span>
                      <span className="access-level">{access.role || 'edit'}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="admin-subsection danger-zone">
                <h3>Danger Zone</h3>
                <p>Permanently delete this family member and all associated data.</p>
                <button
                  onClick={handleDeleteMember}
                  className="btn btn-danger"
                >
                  Delete Member
                </button>
              </div>
            </div>
          </CollapsibleSection>
        )}

        {showInviteModal && (
          <InviteModal
            member={member}
            onClose={() => setShowInviteModal(false)}
          />
        )}
      </div>
    </div>
  );
}

export default MemberDetail;
